import { ModuleEnum, PermissonEnum } from '~/constants/systemEnum'
import type { RoleModuleDto } from '~/core/services/ApiClient'
import { hasEffectivePermission } from '~/core/helpers/permissionEffective'

type PermissionInput = number | PermissonEnum
type ModuleInput = number | ModuleEnum

function toNumber(value: PermissionInput | ModuleInput): number {
  return typeof value === 'number' ? value : (value as number)
}

function getModulePermissions(
  userPermissions: RoleModuleDto[] | undefined,
  module: number,
): RoleModuleDto | undefined {
  return userPermissions?.find((item) => item.module === module)
}

export function usePermission() {
  const systemStore = useSystemStore()

  const userPermissions = computed(
    () => systemStore.systemData?.userPermissions ?? [],
  )

  const isReady = computed(() => systemStore.isInited)

  const can = (module: ModuleInput, permission: PermissionInput): boolean => {
    const moduleValue = toNumber(module)
    const permissionValue = toNumber(permission)
    const moduleData = getModulePermissions(userPermissions.value, moduleValue)
    return hasEffectivePermission(moduleData, permissionValue)
  }

  const canAny = (module: ModuleInput, permissions: PermissionInput[]): boolean =>
    permissions.some((permission) => can(module, permission))

  const canAll = (module: ModuleInput, permissions: PermissionInput[]): boolean =>
    permissions.every((permission) => can(module, permission))

  const canRead = (module: ModuleInput) => can(module, PermissonEnum.Read)
  const canCreate = (module: ModuleInput) => can(module, PermissonEnum.Create)
  const canUpdate = (module: ModuleInput) => can(module, PermissonEnum.Update)
  const canDelete = (module: ModuleInput) => can(module, PermissonEnum.Delete)
  const canEditing = (module: ModuleInput) => can(module, PermissonEnum.Editing)
  const canAccess = (module: ModuleInput) =>
    can(module, PermissonEnum.Allow) || canRead(module)

  return {
    userPermissions,
    isReady,
    can,
    canAny,
    canAll,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    canEditing,
    canAccess,
    ModuleEnum,
    PermissonEnum,
  }
}
